import { action, computed, makeObservable, observable } from 'mobx';
import { BindingProps, StoreNode } from '../../store';
import { Store } from '../../store/store';
import { InputState, input } from '../input';
import { JobModel, Team } from '../../entities';
import { isNonEmptyString } from '../../core';
import { PlayerFramesetState, PlayerSectionName } from '../playerFrameset';
import { notifyError } from '../../services/notifications';
import debounce from 'lodash/debounce';

type Props = BindingProps<{
  jobId: string,
  frameset: PlayerFramesetState,
  teamId?: string | null,
  refreshSearchMatches: () => void,
  clearSearch: () => void
}>

export type SearchMode = 'idle' | 'ask' | 'transcript';

export class PlayerSearchBarState
  extends StoreNode {
  constructor(store: Store, props: Props) {
    super(store, props);
    makeObservable<this, 'refreshSearchMatches' | 'clearTranscriptSearch'>(this, {
      showDropdown: observable,
      dropdownElement: observable,
      searchMode: observable,
      aiAnswerLoading: observable,
      aiSearchKey: observable,
      aiAnswer: observable,
      teamId: computed,
      jobId: computed,
      frameset: computed,
      refreshSearchMatches: computed,
      clearTranscriptSearch: computed,
      dropdownOpened: computed,
      job: computed,
      team: computed,
      isWidgetMode: computed,
      isAiProcessing: computed,
      cliprGptEnabled: computed,
      isTranscriptSearchMode: computed,
      isAskSearchMode: computed,
      isIdleSearchMode: computed,
      placeholder: computed,
      showIcon: computed,
      setShowDropdown: action,
      setDropdownElement: action,
      handleAsk: action,
      handleSearch: action,
      handleKeyUp: action,
      handleBackToTranscriptButtonClick: action,
      resetSearchMode: action,
      reset: action,
      handleClear: action,
    });

    this.searchQuery = input(this, {
      name: 'searchQuery',
      onChange: () => {
        if (this.isTranscriptSearchMode) 
          this.refreshSearchMatchesDebounced();
      }
    });
  }

  readonly searchQuery: InputState;
  showDropdown: boolean = false;
  dropdownElement: HTMLElement | null = null;
  searchMode: SearchMode = 'idle';
  aiAnswerLoading: boolean = false;
  aiSearchKey: string | null = null;
  aiAnswer: string | null = null;

  get teamId(): string | null {
    return this.resolvedProps.teamId ?? null;
  }

  get jobId() {
    return this.resolvedProps.jobId;
  }
  
  get frameset(): PlayerFramesetState | null {
    return this.resolvedProps.frameset ?? null;
  }

  private get refreshSearchMatches(): () => void {
    return this.getResolvedProp('refreshSearchMatches', null);
  }

  private get clearTranscriptSearch(): (invokePause?: boolean | null) => void {
    return this.getResolvedProp('clearSearch', null);
  }

  get dropdownOpened(): boolean {
    return !!(this.showDropdown && this.searchQuery.value);
  }
  
  get job(): JobModel | null {
    return this.store.maybeGetJob(this.jobId);
  }

  get team(): Team | null {
    return this.store.teamManager.getTeam(this.teamId);
  }

  get isWidgetMode() {
    return this.store.widgetService.isWidgetMode;
  }

  get isAiProcessing() {
    const gptProvider = this.store.aiService.getAiProvider(this.job, this.teamId);

    if (gptProvider)
      return this.job?.isAiProcessing(gptProvider);

    return false;
  }

  get cliprGptEnabled() {
    return this.store.aiService.getCliprGptEnabled(this.team);
  }

  get isTranscriptSearchMode() {
    return this.cliprGptEnabled ? this.searchMode === 'transcript' : true;
  }

  get isAskSearchMode() {
    return this.searchMode === 'ask';
  }

  get isIdleSearchMode() {
    return this.cliprGptEnabled && this.searchMode === 'idle';
  }

  get placeholder() {
    if (this.cliprGptEnabled && this.isAskSearchMode) 
      return 'Ask CLIPr GPT anything';

    return this.cliprGptEnabled ? 'Ask anything or search in transcript' : 'Search in transcript';
  }

  get showIcon() {
    return !!this.searchQuery.value || !this.isIdleSearchMode;
  }

  private refreshSearchMatchesDebounced = debounce(() => this.refreshSearchMatches(), 500);

  setShowDropdown = (show: boolean) => {
    this.showDropdown = show;
  }

  setDropdownElement = (dropdownElement: HTMLElement | null) => {
    this.dropdownElement = dropdownElement;
  }

  handleAsk = async (evt: React.MouseEvent | React.KeyboardEvent, aiSearchKey?: string) => {
    evt.stopPropagation();
    this.setShowDropdown(false);
  
    this.configureUIForMobile();
  
    const query = aiSearchKey ?? this.searchQuery.value;
    if (this.isQueryInvalid(query)) 
      return;
  
    this.prepareForAiQuery(query);
    const response = await this.fetchAiAnswer(query!);
  
    if (response.error) {
      notifyError(this, 'Cannot extract answer. Try again.');
    } else {
      this.aiAnswer = response.answer?.result ?? null;
    }
  }
  
  private configureUIForMobile() {
    if (!this.store.ui.isMobile) 
      return;
  
    ['Index', 'Comments', 'Toolbar'].forEach(section => this.frameset?.hideSection(section as PlayerSectionName));
  }
  
  private isQueryInvalid(query?: string | null): boolean {
    return !query || !isNonEmptyString(query) || this.aiAnswerLoading;
  }
  
  private prepareForAiQuery(query: string | null) {
    this.searchMode = 'ask';
    this.aiAnswerLoading = true;
    this.aiSearchKey = query;
    this.searchQuery.value = null;
  }
  
  private async fetchAiAnswer(query: string) {
    const { user: profile } = this.store;
    const userId = !this.teamId ? profile?.id : undefined;
    const [res, err] = await this.store.api.getGPTAnswer({
      input: {
        question: query,
        jobId: this.jobId,
        teamId: this.teamId ?? undefined,
        userId: userId ?? undefined
      }
    });
  
    this.aiAnswerLoading = false;
    if (!res || err) 
      return { error: true };
    
    return { answer: res.getGPTAnswer };
  }

  handleSearch = (evt: React.MouseEvent) => {
    evt.stopPropagation();
    this.setShowDropdown(false);
    this.aiSearchKey = null;
    this.aiAnswer = null;
    this.searchMode = 'transcript';
    this.refreshSearchMatches();
  }

  handleKeyUp = (evt: React.KeyboardEvent) => {   
    if (!this.searchQuery.value) 
      return;

    if (this.searchMode === 'idle' && this.cliprGptEnabled) {
      this.setShowDropdown(true);
    }

    if (this.searchMode === 'ask' && evt.key === 'Enter') {
      this.handleAsk(evt, this.searchQuery.value);
      // Workaround to hide the keyboard on mobile
      (evt.target as HTMLElement).blur?.();
    }
  }

  handleBackToTranscriptButtonClick = () => {
    this.searchQuery.value = null;
    this.resetSearchMode();
    this.clearTranscriptSearch(false);
  }

  resetSearchMode = () => {
    this.aiSearchKey = null;
    this.aiAnswer = null;
    this.searchMode = 'idle';
  }

  reset = () => {
    this.searchQuery.value = null;
    this.resetSearchMode();
  }

  handleClear = () => {
    this.searchQuery.value = null;

    if (this.cliprGptEnabled) {
      return;
    }

    this.resetSearchMode();
    this.clearTranscriptSearch(false);
  }
}
